import { useCallback, useRef, useState } from 'react'

/**
 * Callback-ref IntersectionObserver. `inView` flips false when the element scrolls
 * off-screen — used to pause animations below the fold (compositor stays idle).
 * Defaults to true so content animates until the observer first reports.
 */
export function useInView<T extends Element>(rootMargin = '120px'): {
  ref: (el: T | null) => void
  inView: boolean
} {
  const [inView, setInView] = useState(true)
  const obs = useRef<IntersectionObserver | null>(null)

  const ref = useCallback(
    (el: T | null) => {
      obs.current?.disconnect()
      if (el && typeof IntersectionObserver !== 'undefined') {
        obs.current = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
          rootMargin,
          threshold: 0.01,
        })
        obs.current.observe(el)
      }
    },
    [rootMargin],
  )

  return { ref, inView }
}
